import { Helmet } from 'react-helmet-async'
import { SITE_URL, SITE_NAME } from '@/utils/constants'

/**
 * Seo component props
 */
export interface SeoProps {
  /** Page title (rendered as "{title} | Leru Systems") */
  title: string
  /** Page meta description (~120-160 characters recommended) */
  description: string
  /** Path of the page relative to the site root, e.g. "/services" */
  path: string
  /** Open Graph image URL, defaults to the site's social share image */
  image?: string
  /** Disable indexing for this specific page (defaults to indexable) */
  noindex?: boolean
}

/**
 * Seo component
 * Sets per-page document title, meta description, canonical URL,
 * and Open Graph/Twitter tags so each route has unique, indexable content.
 */
export const Seo = ({
  title,
  description,
  path,
  image = '/assets/images/og-image.jpg',
  noindex = false,
}: SeoProps) => {
  const canonicalUrl = `${SITE_URL}${path === '/' ? '' : path}`
  const fullTitle = path === '/' ? title : `${title} | ${SITE_NAME}`

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={`${SITE_URL}${image}`} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${SITE_URL}${image}`} />
    </Helmet>
  )
}
